import { useMemo } from "react";
import type { WalletClient } from "viem";
import { useAccount, useWalletClient } from "wagmi";
import type { OrderSigner } from "@hyperhedge/polymarket";

/**
 * Bridge between wagmi (Privy embedded wallet) and the venue adapters.
 *
 * Returns:
 *   - `address`           — connected wallet, or null
 *   - `walletClient`      — viem WalletClient (passed straight to HyperliquidAdapter)
 *   - `polymarketSigner`  — adapter satisfying @hyperhedge/polymarket's OrderSigner
 *
 * `signTypedData` on the wallet client is what triggers the user's wallet
 * popup. EIP-712 signing is offline — the user does NOT need to be on
 * Polygon to sign a Polygon-domain order; chainId rides in the typed-data.
 */
export type TradingSigner = {
  address: `0x${string}`;
  walletClient: WalletClient;
  polymarketSigner: OrderSigner;
};

export function useTradingSigner(): TradingSigner | null {
  const { address } = useAccount();
  const { data: walletClient } = useWalletClient();

  return useMemo<TradingSigner | null>(() => {
    if (!walletClient || !address) return null;

    const polymarketSigner: OrderSigner = {
      address,
      signTypedData: async (params) => {
        // viem's signTypedData has strongly-conditional types — cast to bypass
        // them since polymarket's signing module describes the EIP-712 shape
        // already.
        const signature = await (
          walletClient.signTypedData as (
            args: Record<string, unknown>,
          ) => Promise<`0x${string}`>
        )({
          account: address,
          domain: params.domain,
          types: params.types,
          primaryType: params.primaryType,
          message: params.message,
        });
        return signature;
      },
    };

    return { address, walletClient, polymarketSigner };
  }, [walletClient, address]);
}
